import React, { useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import './MonitGPS.css';
import { useLanguage } from './LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';
import T from './translations';

const MonitGPSLanding = () => {
  const { language } = useLanguage();
  const TT = T[language];

  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const ScrollReveal = ({ children, delay = 0 }) => {
    const ref = React.useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 50 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    );
  };

  const featureIcons = ['📍', '🗺️', '🚨', '🖥️'];
  const featureGradients = [
    'from-cyan-500/20 to-blue-500/20',
    'from-blue-500/20 to-cyan-500/20',
    'from-cyan-600/20 to-blue-600/20',
    'from-blue-600/20 to-cyan-600/20',
  ];
  const features = TT.features.items.map((item, i) => ({
    ...item,
    icon: featureIcons[i],
    gradient: featureGradients[i],
  }));

  const serviceIcons = ['🗺️', '🖥️', '🏆'];
  const services = TT.services.items.map((item, i) => ({
    ...item,
    icon: serviceIcons[i],
    highlighted: i === 2,
  }));

  const gallery = TT.gallery.items.map((item, i) => ({
    ...item,
    src: [
      '/images/gallery/ckr-1.jpg',
      '/images/gallery/ckr-2.jpg',
      '/images/gallery/ckr-3.jpg',
      '/images/gallery/ckr-4.jpg',
    ][i],
  }));

  const partners = ['RAJD BARBÓRKI CIESZYŃSKIEJ', 'RAJD WISŁY', 'RAJD MAŁOPOLSKI'];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-carbon">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>

        <motion.div
          className="absolute top-1/4 -left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[100px]"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        {/* Navigation */}
        <motion.nav
          className="absolute top-0 left-0 right-0 z-50 glass-effect"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/images/logominitgps.png" alt="MonitGPS Logo" className="h-12 w-auto" />
            </div>

            <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
              <a href="#features" className="hover:text-cyan-400 transition-colors">{TT.nav.features}</a>
              <a href="#services" className="hover:text-cyan-400 transition-colors">{TT.nav.services}</a>
              <a href="#online-results" className="hover:text-cyan-400 transition-colors">{TT.nav.onlineResults}</a>
              <a href="#requirements" className="hover:text-cyan-400 transition-colors">{TT.nav.requirements}</a>
              <a href="#contact" className="hover:text-cyan-400 transition-colors">{TT.nav.contact}</a>
              <a href="/zawodnik" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-400 transition-colors text-cyan-500 font-bold">{TT.nav.competitor}</a>
              <a href="https://monitgps.pl/tracking/" className="hover:text-cyan-400 transition-colors">{TT.nav.rallyControl}</a>
              <LanguageSwitcher />
              <a href="#contact">
                <button className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all">
                  {TT.nav.contactBtn}
                </button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-3">
              <LanguageSwitcher />
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-cyan-400 focus:outline-none">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden bg-[#0a0a0a]/95 border-b border-white/10 flex flex-col px-6 py-4 space-y-4 text-sm font-medium backdrop-blur-xl">
              <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-cyan-400">{TT.nav.features}</a>
              <a href="#services" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-cyan-400">{TT.nav.services}</a>
              <a href="#online-results" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-cyan-400">{TT.nav.onlineResults}</a>
              <a href="#requirements" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-cyan-400">{TT.nav.requirements}</a>
              <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-cyan-400">{TT.nav.contact}</a>
              <a href="/zawodnik" className="text-cyan-500 font-bold hover:text-cyan-400">{TT.nav.competitor}</a>
              <a href="https://monitgps.pl/tracking/" className="hover:text-cyan-400">{TT.nav.rallyControl}</a>
            </div>
          )}
        </motion.nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <motion.div
              className="inline-block mb-6 px-4 py-2 glass-effect rounded-full text-sm font-medium"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-cyan-400">●</span> {TT.hero.badge}
            </motion.div>
          </motion.div>

          <motion.h1
            className="font-display font-black text-6xl md:text-8xl leading-tight mb-8"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {TT.hero.line1a} <span className="text-gradient">{TT.hero.line1b}</span>
            <br />
            {TT.hero.line2a} <span className="text-gradient">{TT.hero.line2b}</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {TT.hero.subtitle}
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <div className="glass-effect rounded-3xl p-4 hover-lift w-48 h-48 sm:w-64 sm:h-64 relative flex items-center justify-center">
              <img
                src="/images/Device1.jpg"
                alt="GPS Device 1"
                className="max-w-full max-h-full object-contain drop-shadow-2xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div className="glass-effect rounded-3xl p-4 hover-lift w-48 h-48 sm:w-64 sm:h-64 relative flex items-center justify-center">
              <img
                src="/images/Device2.jpg"
                alt="GPS Device 2"
                className="max-w-full max-h-full object-contain drop-shadow-2xl"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <a href="#contact">
              <button className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-lg overflow-hidden hover:shadow-2xl hover:shadow-cyan-500/50 transition-all">
                <span className="relative z-10">{TT.hero.ctaOrder}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </button>
            </a>
            <a href="#features">
              <button className="group px-8 py-4 glass-effect rounded-2xl font-display font-bold text-lg hover-lift flex items-center space-x-2">
                <span>{TT.hero.ctaFeatures}</span>
                <span className="text-2xl group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </a>
          </motion.div>

          <motion.div
            className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{ opacity }}
          >
            <div className="w-6 h-10 border-2 border-cyan-500/50 rounded-full p-1">
              <div className="w-1.5 h-3 bg-cyan-400 rounded-full mx-auto animate-pulse"></div>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50 scan-line"></div>
      </section>

      {/* Live Stats Bar */}
      <ScrollReveal>
        <section className="py-12 glass-effect border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
              <div className="group">
                <div className="font-display font-black text-5xl md:text-6xl text-gradient mb-2">
                  {TT.stats.maintenanceFreeValue}
                </div>
                <div className="text-gray-400 font-medium">{TT.stats.maintenanceFreeLabel}</div>
                <div className="mt-2 text-xs text-cyan-500/70">{TT.stats.maintenanceFreeDesc}</div>
              </div>
              <div className="group">
                <div className="font-display font-black text-5xl md:text-6xl text-gradient mb-2">
                  {TT.stats.realtimeValue}
                </div>
                <div className="text-gray-400 font-medium">{TT.stats.realtimeLabel}</div>
                <div className="mt-2 text-xs text-cyan-500/70">{TT.stats.realtimeDesc}</div>
              </div>
              <div className="group">
                <div className="font-display font-black text-5xl md:text-6xl text-gradient mb-2">
                  {TT.stats.serviceValue}
                </div>
                <div className="text-gray-400 font-medium">{TT.stats.serviceLabel}</div>
                <div className="mt-2 text-xs text-cyan-500/70">{TT.stats.serviceDesc}</div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Core Features */}
      <section id="features" className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.features.sectionBadge}
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                {TT.features.sectionTitle1} <span className="text-gradient">{TT.features.sectionTitle2}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {TT.features.sectionSubtitle}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <ScrollReveal key={index} delay={index * 0.1}>
                <motion.div
                  className={`glass-effect rounded-3xl p-8 hover-lift cursor-pointer relative overflow-hidden ${
                    index === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
                  }`}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 hover:opacity-100 transition-opacity`}></div>
                  <div className="relative z-10">
                    <div className="text-6xl mb-6">{feature.icon}</div>
                    <h3 className="font-display font-bold text-2xl md:text-3xl mb-4">{feature.title}</h3>
                    <p className="text-gray-400 leading-relaxed mb-6">{feature.description}</p>
                    <div className="mt-6 text-cyan-400 font-display font-bold text-sm flex items-center group">
                      <span>{TT.features.learnMore}</span>
                      <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
                    </div>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Services / Pricing Section */}
      <section id="services" className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.3, 0.2] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.services.sectionBadge}
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                {TT.services.sectionTitle1} <span className="text-gradient">{TT.services.sectionTitle2}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {TT.services.sectionSubtitle}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {services.map((service, index) => (
              <ScrollReveal key={index} delay={index * 0.15}>
                <motion.div
                  className={`pricing-card-glow glass-effect rounded-3xl p-8 relative ${
                    service.highlighted ? 'border-2 border-cyan-500 glow-effect-strong' : ''
                  }`}
                  whileHover={{ y: -8 }}
                >
                  {service.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className="px-4 py-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full text-sm font-bold">
                        {TT.services.mostPopular}
                      </div>
                    </div>
                  )}
                  <div className="mb-6 text-center">
                    <div className="text-6xl mb-4">{service.icon}</div>
                    <h3 className="font-display font-bold text-2xl mb-2">{service.name}</h3>
                    <p className="text-gray-500 text-sm">{service.description}</p>
                  </div>
                  <div className="mb-8 text-center">
                    <div className="font-display font-black text-3xl text-gradient">{service.price}</div>
                    <p className="text-sm text-gray-500 mt-2">{TT.services.contactForDetails}</p>
                  </div>
                  <ul className="space-y-4 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start">
                        <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">✓</span>
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="#contact">
                    <button
                      className={`w-full py-4 rounded-xl font-display font-bold transition-all ${
                        service.highlighted
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-cyan-500/50'
                          : 'glass-effect hover:border-cyan-500'
                      }`}
                    >
                      {TT.services.requestQuote}
                    </button>
                  </a>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Online Results Section */}
      <section id="online-results" className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.onlineResults.sectionBadge}
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                {TT.onlineResults.sectionTitle1} <span className="text-gradient">{TT.onlineResults.sectionTitle2}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {TT.onlineResults.sectionSubtitle}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8">
                <h3 className="font-display font-bold text-3xl mb-6 text-cyan-400">
                  {TT.onlineResults.card1Title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">{TT.onlineResults.card1Text1}</p>
                <p className="text-gray-400 leading-relaxed mb-8">{TT.onlineResults.card1Text2}</p>
                <div className="space-y-3">
                  {TT.onlineResults.features.slice(0, 4).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1">✓</span>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8">
                <h3 className="font-display font-bold text-3xl mb-6 text-cyan-400">
                  {TT.onlineResults.card2Title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">{TT.onlineResults.card2Text1}</p>
                <p className="text-gray-400 leading-relaxed mb-8">{TT.onlineResults.card2Text2}</p>
                <div className="space-y-3">
                  {TT.onlineResults.features.slice(4).map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1">✓</span>
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.6}>
            <div className="mt-12 text-center">
              <a href="#contact">
                <button className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all">
                  {TT.onlineResults.ctaBtn}
                </button>
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Requirements Section */}
      <section id="requirements" className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.requirements.sectionBadge}
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                {TT.requirements.sectionTitle1} <span className="text-gradient">{TT.requirements.sectionTitle2}</span>
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-8">
            <ScrollReveal delay={0.2}>
              <div className="glass-effect rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-2xl">
                    📋
                  </div>
                  <h3 className="font-display font-bold text-2xl">{TT.requirements.card1Title}</h3>
                </div>
                <ul className="space-y-4">
                  {TT.requirements.requirementsList.map((req, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">•</span>
                      <span className="text-gray-300">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.4}>
              <div className="glass-effect rounded-3xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center text-2xl">
                    ✓
                  </div>
                  <h3 className="font-display font-bold text-2xl">{TT.requirements.card2Title}</h3>
                </div>
                <ul className="space-y-4">
                  {TT.requirements.provideList.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-cyan-400 mr-3 mt-1 flex-shrink-0">✓</span>
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={0.6}>
            <div className="mt-12 glass-effect rounded-3xl p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="text-5xl mb-4">⚡</div>
                <h3 className="font-display font-bold text-2xl mb-4">{TT.requirements.infoTitle}</h3>
                <p className="text-gray-400 leading-relaxed">{TT.requirements.infoText}</p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.gallery.sectionBadge}
              </div>
              <h2 className="font-display font-black text-5xl md:text-7xl mb-6">
                {TT.gallery.sectionTitle1} <span className="text-gradient">{TT.gallery.sectionTitle2}</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                {TT.gallery.sectionSubtitle}
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {gallery.map((item, index) => (
              <ScrollReveal key={index} delay={index * 0.15}>
                <motion.div
                  className="glass-effect rounded-3xl overflow-hidden hover-lift"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={item.src}
                      alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 p-6">
                      <div className="font-display font-bold text-2xl text-cyan-400 mb-1">{item.title}</div>
                      <div className="text-gray-200/80">{item.desc}</div>
                    </div>
                  </div>
                  <div className="p-6 border-t border-cyan-500/10">
                    <h3 className="font-display font-bold text-xl mb-2">{item.title}</h3>
                    <p className="text-gray-400">{item.desc}</p>
                  </div>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 grid-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-block px-4 py-2 glass-effect rounded-full text-sm font-medium text-cyan-400 mb-6">
                {TT.partners.sectionBadge}
              </div>
              <h2 className="font-display font-black text-4xl md:text-6xl mb-6">
                {TT.partners.sectionTitle}
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
              {partners.map((partner, index) => (
                <motion.div
                  key={index}
                  className="glass-effect rounded-2xl p-8 flex items-center justify-center hover-lift cursor-pointer"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="text-center">
                    <div className="font-display font-bold text-lg text-gray-400">{partner}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact" className="py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20"></div>
        <motion.div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-[150px]"
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity }}
        />

        <div className="max-w-5xl mx-auto text-center relative z-10">
          <ScrollReveal>
            <motion.div
              animate={{ rotate: [0, 5, 0, -5, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
              className="text-8xl mb-8"
            >
              🏁
            </motion.div>

            <h2 className="font-display font-black text-5xl md:text-8xl mb-8 leading-tight">
              {TT.contact.sectionTitle1} <span className="text-gradient">{TT.contact.sectionTitle2}</span>
            </h2>

            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {TT.contact.subtitle}
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="glass-effect rounded-2xl p-6">
                <div className="text-4xl mb-3">📱</div>
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Maciek</div>
                <a href="tel:504178476" className="text-gray-300 hover:text-cyan-400 transition-colors">504 178 476</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="text-4xl mb-3">📱</div>
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Adrian</div>
                <a href="tel:791807484" className="text-gray-300 hover:text-cyan-400 transition-colors">791 807 484</a>
              </div>
              <div className="glass-effect rounded-2xl p-6">
                <div className="text-4xl mb-3">📱</div>
                <div className="font-display font-bold text-lg mb-2 text-cyan-400">Dawid</div>
                <a href="tel:603197643" className="text-gray-300 hover:text-cyan-400 transition-colors">603 197 643</a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
              <a href="tel:504178476">
                <button className="group relative px-12 py-5 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl font-display font-bold text-xl overflow-hidden glow-effect-strong">
                  <span className="relative z-10 flex items-center">
                    {TT.contact.callNow}
                    <span className="ml-2 text-2xl">📞</span>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </button>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-500">
              <div className="flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                {TT.contact.freeQuote}
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                {TT.contact.vatInvoice}
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-2">✓</span>
                {TT.contact.fullSupport}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 glass-effect border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="/images/logominitgps.png" alt="MonitGPS Logo" className="h-12 w-auto" />
              <p className="text-gray-500 text-sm mt-2">{TT.footer.description}</p>
            </div>
            <div>
              <h4 className="font-display font-bold mb-4">{TT.footer.services}</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#services" className="hover:text-cyan-400 transition-colors">{TT.footer.rallyRecon}</a></li>
                <li><a href="#services" className="hover:text-cyan-400 transition-colors">{TT.footer.monitoringCKR}</a></li>
                <li><a href="#online-results" className="hover:text-cyan-400 transition-colors">{TT.footer.onlineResults}</a></li>
                <li><a href="#requirements" className="hover:text-cyan-400 transition-colors">{TT.footer.requirements}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold mb-4">{TT.footer.contact}</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="tel:504178476" className="hover:text-cyan-400 transition-colors">Maciek: 504 178 476</a></li>
                <li><a href="tel:791807484" className="hover:text-cyan-400 transition-colors">Adrian: 791 807 484</a></li>
                <li><a href="tel:603197643" className="hover:text-cyan-400 transition-colors">Dawid: 603 197 643</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-display font-bold mb-4">{TT.footer.system}</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li className="hover:text-cyan-400 transition-colors">{TT.footer.maintenanceFreeDevices}</li>
                <li className="hover:text-cyan-400 transition-colors">{TT.footer.realtimeMonitoring}</li>
                <li className="hover:text-cyan-400 transition-colors">{TT.footer.tagHeuerIntegration}</li>
                <li className="hover:text-cyan-400 transition-colors">{TT.footer.support247}</li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between text-sm text-gray-500">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p>{TT.footer.copyright}</p>
              <a href="/polityka-prywatnosci" className="hover:text-cyan-400 transition-colors underline decoration-white/20 underline-offset-4">
                {TT.footer.privacyPolicy}
              </a>
            </div>
            <div className="mt-4 md:mt-0">
              <p>{TT.footer.tagline}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MonitGPSLanding;
